import {
  AlertCircle,
  Calendar,
  Check,
  CreditCard,
  DollarSign,
  TrendingUp,
  X,
  Zap
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import subscriptionService from '../../services/subscriptionService';
import PaymentMethodsManagement from '../PaymentMethodsManagement';
import SquarePaymentForm from '../SquarePaymentForm';

const SubscriptionManagement = () => {
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [quotaSummary, setQuotaSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('blog');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [activeTab, setActiveTab] = useState('plans'); // 'plans' or 'payment-methods'

  useEffect(() => {
    loadSubscriptionData();
  }, [selectedType]);

  const loadSubscriptionData = async () => {
    setLoading(true);
    try {
      // Load all plans
      const plansRes = await subscriptionService.getAllPlans(selectedType);
      setAvailablePlans(plansRes.plans || []);

      // Load current subscription
      try {
        const subRes = await subscriptionService.getUserSubscription(selectedType);
        if (subRes.hasSubscription) {
          setCurrentSubscription(subRes.subscription);

          // Load quota summary
          const quotaRes = await subscriptionService.getQuotaSummary(selectedType);
          setQuotaSummary(quotaRes);
        } else {
          setCurrentSubscription(null);
          setQuotaSummary(null);
        }
      } catch (error) {
        console.log('No active subscription');
        setCurrentSubscription(null);
        setQuotaSummary(null);
      }
    } catch (error) {
      console.error('Error loading subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = (plan) => {
    setSelectedPlan(plan);
    setShowUpgradeModal(false);  // Close upgrade modal
    setShowPaymentModal(true);    // Open payment modal
    setPaymentError('');
  };

  const handlePaymentSubmit = async (paymentDetails) => {
    if (!selectedPlan) return;

    setPaymentLoading(true);
    setPaymentError('');

    try {
      await subscriptionService.createSubscription(
        selectedPlan._id,
        selectedType,
        paymentDetails
      );

      setShowPaymentModal(false);
      alert('Subscription created successfully!');
      loadSubscriptionData();
    } catch (error) {
      console.error('Subscription error:', error);
      setPaymentError(error.message || 'Failed to upgrade subscription');
    } finally {
      setPaymentLoading(false);
    }
  };

  const confirmUpgrade = () => {
    setShowUpgradeModal(false);
    if (selectedPlan) {
      handleUpgrade(selectedPlan);
    }
  };

  const getUsagePercentage = (used, limit) => {
    if (limit === 0) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Subscription Management</h1>
        <p className="text-gray-600">Manage your subscription plans and payment methods</p>
      </div>

      {/* Tab Selector */}
      <div className="mb-6 flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('plans')}
          className={`px-4 py-3 font-medium transition-colors border-b-2 ${activeTab === 'plans'
            ? 'border-blue-500 text-blue-600'
            : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
        >
          <span className="flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Subscription Plans
          </span>
        </button>
        <button
          onClick={() => setActiveTab('payment-methods')}
          className={`px-4 py-3 font-medium transition-colors border-b-2 ${activeTab === 'payment-methods'
            ? 'border-blue-500 text-blue-600'
            : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
        >
          <span className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            Payment Methods
          </span>
        </button>
      </div>

      {/* Payment Methods Tab */}
      {activeTab === 'payment-methods' && (
        <PaymentMethodsManagement />
      )}

      {/* Subscription Plans Tab */}
      {activeTab === 'plans' && (
        <>
          {/* Type Selector */}
          <div className="mb-6 flex gap-2">
            {['blog', 'podcast', 'video'].map((type) => (
              <button
                key={type}
                onClick={() => setSelectedType(type)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${selectedType === type
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>

          {/* Current Subscription */}
          {currentSubscription && (
            <div className="mb-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold mb-1">
                    {currentSubscription.planId?.displayName || 'Current Plan'}
                  </h2>
                  <p className="text-blue-100">
                    {currentSubscription.planId?.description}
                  </p>
                </div>
                <div className="bg-white/20 px-4 py-2 rounded-lg">
                  <div className="text-3xl font-bold">
                    ${currentSubscription.planId?.price || 0}
                  </div>
                  <div className="text-sm text-blue-100">CAD/month</div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                <div className="bg-white/10 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm text-blue-100">Status</span>
                  </div>
                  <div className="text-lg font-semibold capitalize">
                    {currentSubscription.status}
                  </div>
                </div>

                <div className="bg-white/10 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm text-blue-100">Current Period</span>
                  </div>
                  <div className="text-lg font-semibold">
                    {new Date(currentSubscription.currentPeriodStart).toLocaleDateString()} - {new Date(currentSubscription.currentPeriodEnd).toLocaleDateString()}
                  </div>
                </div>

                <div className="bg-white/10 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Zap className="w-4 h-4" />
                    <span className="text-sm text-blue-100">Type</span>
                  </div>
                  <div className="text-lg font-semibold capitalize">
                    {currentSubscription.type}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Usage Summary */}
          {quotaSummary && quotaSummary.quotas && (
            <div className="mb-8 bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-500" />
                Usage Overview
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(quotaSummary.quotas).map(([key, quota]) => {
                  const percentage = getUsagePercentage(quota.used, quota.limit);
                  const isUnlimited = quota.limit === -1;

                  return (
                    <div key={key} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="font-medium text-gray-900 text-sm">
                          {key.replace(/([A-Z])/g, ' $1').trim().replace(/^./, str => str.toUpperCase())}
                        </div>
                        <div className="text-sm font-semibold text-gray-700">
                          {quota.used} / {isUnlimited ? '∞' : quota.limit}
                        </div>
                      </div>

                      {!isUnlimited && (
                        <>
                          <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                            <div
                              className={`h-2 rounded-full transition-all ${getUsageColor(percentage)}`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <div className="flex justify-between text-xs text-gray-500">
                            <span>{percentage.toFixed(0)}% used</span>
                            <span>{quota.remaining} remaining</span>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* No Subscription Message */}
          {!currentSubscription && !loading && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-yellow-900 mb-1">
                    No Active Subscription
                  </h3>
                  <p className="text-yellow-800">
                    You don't have an active {selectedType} subscription. Choose a plan below to get started!
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Available Plans */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Available Plans</h2>

            {availablePlans.length === 0 ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">No {selectedType} plans available at the moment.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {availablePlans.map((plan) => {
                  const isCurrentPlan = currentSubscription?.planId?._id === plan._id;

                  return (
                    <div
                      key={plan._id}
                      className={`relative rounded-xl shadow-lg overflow-hidden transition-transform hover:scale-105 ${plan.popular
                        ? 'border-2 border-blue-500'
                        : 'border border-gray-200'
                        }`}
                    >
                      {plan.popular && (
                        <div className="absolute top-0 right-0 bg-blue-500 text-white px-3 py-1 text-sm font-bold">
                          POPULAR
                        </div>
                      )}

                      <div className="p-6 bg-white">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">
                          {plan.displayName}
                        </h3>
                        <p className="text-gray-600 mb-4 min-h-[48px]">
                          {plan.description}
                        </p>

                        <div className="mb-6">
                          <div className="flex items-baseline gap-1">
                            <span className="text-4xl font-bold text-gray-900">
                              ${plan.price}
                            </span>
                            <span className="text-gray-600">CAD</span>
                            <span className="text-gray-500 text-sm">/ month</span>
                          </div>
                        </div>

                        {/* Highlights */}
                        <ul className="space-y-2 mb-6">
                          {plan.highlights.slice(0, 5).map((highlight, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm">
                              <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                              <span className="text-gray-700">{highlight}</span>
                            </li>
                          ))}
                        </ul>

                        <button
                          onClick={() => handleUpgrade(plan)}
                          disabled={isCurrentPlan}
                          className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${isCurrentPlan
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : plan.popular
                              ? 'bg-blue-500 text-white hover:bg-blue-600'
                              : 'bg-gray-900 text-white hover:bg-gray-800'
                            }`}
                        >
                          {isCurrentPlan ? 'Current Plan' : 'Upgrade Now'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && selectedPlan && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Confirm Upgrade
            </h3>

            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                You are about to upgrade to the <strong>{selectedPlan.displayName}</strong> plan.
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 text-blue-900 mb-2">
                  <DollarSign className="w-5 h-5" />
                  <span className="font-semibold">Pricing Details</span>
                </div>
                <div className="text-3xl font-bold text-blue-900">
                  ${selectedPlan.price} <span className="text-base font-normal">CAD/month</span>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Plan Includes:</h4>
                <ul className="space-y-1">
                  {selectedPlan.highlights.slice(0, 4).map((highlight, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      {highlight}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmUpgrade}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Confirm Upgrade
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedPlan && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Complete Payment</h3>
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setPaymentError('');
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
                disabled={paymentLoading}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Plan Summary */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-700 font-medium">{selectedPlan.displayName}</span>
                <span className="text-2xl font-bold text-gray-900">${selectedPlan.price}</span>
              </div>
              <p className="text-sm text-gray-600">Billed monthly • You can cancel anytime</p>
            </div>

            {/* Error Message */}
            {paymentError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-900">Payment Failed</p>
                  <p className="text-sm text-red-700 mt-1">{paymentError}</p>
                </div>
              </div>
            )}

            {/* Square Payment Form */}
            <SquarePaymentForm
              amount={selectedPlan.price}
              onSuccess={handlePaymentSubmit}
              onError={(error) => setPaymentError(error)}
              buttonText={`Subscribe for $${selectedPlan.price}/month`}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionManagement;


